import { useCallback, useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/api";
import { AutoTradingAdminUnlock } from "@/components/AutoTradingAdminUnlock";
import { TradingAccountWorkspace } from "@/components/TradingAccountWorkspace";

const emptyAccount = { waiting_entries: [], executions: [], actions: [], virtual_lots: [], eligible_sources: [] };
const empty = { ...emptyAccount, live: { ...emptyAccount, environment: "LIVE", arming_state: "DISARMED" } };
const messages = {
  CONNECTED: "OKX Demo 连接、交易权限与多空双向模式已确认。",
  CREDENTIALS_NOT_CONFIGURED: "尚未配置 OKX Demo API。",
  INVALID_API_CREDENTIALS: "API Key 或 Secret Key 无效。",
  INVALID_PASSPHRASE: "Passphrase 无效。",
  TRADE_PERMISSION_MISSING: "API 缺少 Trade 权限。",
  READ_PERMISSION_MISSING: "API 缺少 Read 权限。",
  HEDGE_MODE_REQUIRED: "账户不是 Long/Short Mode；程序不会自动修改账户模式。",
  DEMO_CONNECTION_FAILED: "OKX Demo 连接失败。",
  LIVE_CONNECTION_FAILED: "OKX Live 连接失败。",
  WITHDRAW_PERMISSION_NOT_ALLOWED: "Live API 不得包含 Withdraw 权限。",
  ACCOUNT_INSTRUMENTS_UNAVAILABLE: "账户可交易 Instruments 读取失败。",
  SYSTEM_PROTECTION_FAILURE: "存在未解决的 Live Protection Failure，不能启用。",
  LIVE_NOT_READY: "Live 尚未完成连接检查，必须先进入 READY。",
  CONNECTION_UNCONFIRMED: "连接安全条件尚未全部满足。",
  ADMIN_AUTH_NOT_CONFIGURED: "Production 管理员认证环境变量未配置。",
  ORIGIN_NOT_ALLOWED: "管理员登录请求来源校验失败。",
  ADMIN_LOGIN_LOCKED: "登录尝试过多，请稍后再试。",
  INVALID_ADMIN_PASSWORD: "管理员密码错误。",
};

export default function AutoTradingPage() {
  const [auth, setAuth] = useState(null);
  const [authConfigured, setAuthConfigured] = useState(true);
  const [data, setData] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [environment, setEnvironment] = useState("DEMO");
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { setData((await api.get("/auto-trading")).data); setError(""); }
    catch (requestError) { if (requestError.response?.status === 401) setAuth(false); else setError("自动交易状态加载失败"); }
  }, []);
  const checkSession = useCallback(async () => {
    try {
      let result = (await api.get("/auto-trading/auth/session")).data;
      setAuthConfigured(result.auth_configured);
      if (!result.authenticated) {
        try { result = (await api.post("/auto-trading/auth/refresh")).data; } catch { /* requires unlock */ }
      }
      setAuth(Boolean(result.authenticated));
      if (result.authenticated) load();
    } catch { setAuth(false); }
  }, [load]);
  useEffect(() => { checkSession(); }, [checkSession]);
  useEffect(() => {
    if (!auth) return undefined;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [auth, load]);
  const login = async (password) => {
    setBusy(true); setError("");
    try { await api.post("/auto-trading/auth/login", { password }); setAuth(true); await load(); }
    catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(messages[detail] || `管理员登录失败（${detail || "NETWORK_ERROR"}）`);
      if (detail === "ADMIN_AUTH_NOT_CONFIGURED") setAuthConfigured(false);
    }
    password = null; setBusy(false);
  };
  const logout = async () => { setBusy(true); try { await api.post("/auto-trading/auth/logout"); } finally { setAuth(false); setData(empty); setBusy(false); } };
  const saveCredentials = (target) => async (credentials) => {
    setBusy(true); setMessage("");
    try {
      const path = target === "LIVE" ? "/auto-trading/live/credentials" : "/auto-trading/credentials";
      const result = (await api.post(path, credentials)).data;
      setMessage(result.ok ? (target === "LIVE"
        ? "Live API 已加密保存；仍为 DISARMED，请执行连接检查进入 READY。"
        : "Demo API 连接已确认并安全保存。")
        : (messages[result.detail] || result.detail || "凭证测试失败"));
      if (result.ok) await load();
      setBusy(false); return Boolean(result.ok);
    } catch (requestError) { setMessage(messages[requestError.response?.data?.detail] || "凭证保存失败"); setBusy(false); return false; }
  };
  const checkConnection = (target) => async () => {
    setBusy(true);
    try { const path = target === "LIVE" ? "/auto-trading/live/check-connection" : "/auto-trading/check-connection"; const result = (await api.post(path)).data; setMessage(target === "LIVE" && result.ok ? "Live 连接检查通过，当前状态 READY；仍未启用真钱交易。" : (messages[result.detail] || result.detail || "连接检查完成")); }
    catch { setMessage("连接检查失败"); }
    setBusy(false); load();
  };
  const deleteCredentials = (target) => async () => { setBusy(true); try { const live = target === "LIVE"; await api.delete(live ? "/auto-trading/live/credentials" : "/auto-trading/credentials", { data: { confirmation: live ? "DELETE_OKX_LIVE_API" : "DELETE_OKX_DEMO_API" } }); setMessage(`OKX ${target} API 已删除，该环境已关闭。`); await load(); } catch { setMessage("删除失败"); } setBusy(false); };
  const changeMaster = async (enabled) => { setBusy(true); try { const result = (await api.post("/auto-trading/master-switch", { enabled })).data; setMessage(result.ok ? (enabled ? "自动执行总开关已开启" : "自动执行总开关已关闭") : (messages[result.detail] || result.detail)); } catch { setMessage("总开关更新失败"); } setBusy(false); load(); };
  const saveEntryTolerance = async (value) => {
    setBusy(true); setMessage("");
    try {
      const result = (await api.post("/auto-trading/settings", {
        reference_entry_adverse_tolerance_percent: Number(value),
      })).data;
      setMessage(result.ok ? `REFERENCE_ENTRY 不利偏差容忍度已更新为 ${result.reference_entry_adverse_tolerance_percent}%` : "参数更新失败");
      await load();
    } catch (requestError) {
      setMessage(requestError.response?.status === 422 ? "请输入 0.10%–2.00% 之间的数值。" : "参数更新失败");
    }
    setBusy(false);
  };
  const setLiveArming = async (action) => {
    setBusy(true); setMessage("");
    try {
      const result = (await api.post("/auto-trading/live/arming", { action })).data;
      setMessage(result.ok ? (action === "EMERGENCY_STOP"
        ? "LIVE 已紧急停止全部交易 API 动作。已有仓位需要人工确认。"
        : action === "DISARM"
          ? "LIVE 已停止新开仓；已有仓位的止损、TP、减仓与对账继续运行。"
          : `LIVE 已进入 ${result.arming_state}`) : (messages[result.detail] || result.detail));
      await load();
    } catch { setMessage("Live Arming 更新失败"); }
    setBusy(false);
  };
  const activeData = environment === "LIVE" ? (data.live || empty.live) : data;
  const handlers = environment === "LIVE" ? {
    saveCredentials: saveCredentials("LIVE"), checkConnection: checkConnection("LIVE"),
    deleteCredentials: deleteCredentials("LIVE"), enableLive: () => setLiveArming("ENABLE"),
    disarmLive: () => setLiveArming("DISARM"),
    emergencyStopLive: () => setLiveArming("EMERGENCY_STOP"),
  } : {
    saveCredentials: saveCredentials("DEMO"), checkConnection: checkConnection("DEMO"),
    deleteCredentials: deleteCredentials("DEMO"), changeMaster,
    saveEntryTolerance,
  };
  return <section data-testid="auto-trading-page">
    <div className="page-heading"><div><p className="eyebrow">OKX Demo / Live 隔离执行系统</p><h1>自动交易</h1></div>
      {auth && <Button variant="ghost" onClick={logout} disabled={busy} data-testid="admin-logout-button"><LogOut aria-hidden="true" />锁定页面</Button>}</div>
    {auth === null && <p className="empty-state bordered" data-testid="admin-session-loading">正在检查管理员会话…</p>}
    {auth === false && <AutoTradingAdminUnlock busy={busy} error={error} configured={authConfigured} onLogin={login} />}
    {auth && <>
      {error && <p className="error-text" data-testid="auto-trading-error">{error}</p>}
      <div className="environment-tabs" role="tablist" data-testid="trading-environment-tabs">
        <Button variant={environment === "DEMO" ? "default" : "outline"}
          onClick={() => setEnvironment("DEMO")} data-testid="demo-environment-tab">DEMO</Button>
        <Button variant={environment === "LIVE" ? "destructive" : "outline"}
          onClick={() => setEnvironment("LIVE")} data-testid="live-environment-tab">LIVE</Button>
      </div>
      {message && <p className="action-message" data-testid="auto-trading-action-message">{message}</p>}
      <TradingAccountWorkspace environment={environment} data={activeData}
        busy={busy} handlers={handlers} />
    </>}
  </section>;
}